'use client'
import NavigateRightIcon from '@/app/icons/navigate-right'
import { AdDecision } from '@/app/util/ads-endpoint'
import Attribution from '@/components/attribution-v2'
import UAParserContext from '@/context/ua-parser'
import { fireAdTrackingPixel, useAdMediaClick } from '@/util/ad-click'
import { getGifHeight, getGifWidth } from '@giphy/js-util'
import { Gif, GifOverlayProps } from '@giphy/react-components'
import { useContext } from 'react'
import ScrollCarousel from '../scroll-carousel'
import { spotlightWidth } from './spotlight-util'

const GridOverlay = ({ gif, carouselData }: GifOverlayProps & { carouselData: AdDecision }) => {
    return (
        <div className="absolute inset-0">
            <Attribution user={gif.user} className="absolute bottom-2 left-2" />
            {carouselData?.cta_url && (
                <div
                    className="absolute right-2 bottom-2 flex h-[50px] items-center justify-center gap-2 rounded-md bg-giphyBlack/50 px-5 font-bold text-giphyWhite hover:bg-giphyBlack/80"
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        fireAdTrackingPixel(carouselData.cta_tracking_url)
                        if (carouselData.cta_url) {
                            window.open(carouselData.cta_url, '_blank', 'noopener,noreferrer')
                        }
                    }}
                >
                    {carouselData.cta_text} <NavigateRightIcon className="size-3 fill-giphyWhite" />
                </div>
            )}
        </div>
    )
}
const MainGif = ({ data, width }: { data: AdDecision; width: number; height?: number }) => {
    const { gif, impression_url, click_tracking_url, click_url } = data
    const onAdMediaClick = useAdMediaClick()
    const aspectRatio = gif.images.original.width / gif.images.original.height
    return (
        <>
            {impression_url && <img src={impression_url} style={{ display: 'none' }} alt="" />}
            <Gif
                gif={gif}
                width={width}
                percentWidth="100%"
                overlay={(props) => <GridOverlay {...props} carouselData={data} />}
                style={{ aspectRatio }}
                lazyLoad={false}
                noLink
                // eslint-disable-next-line better-tailwindcss/no-unregistered-classes
                className="giphy-spotlight-grid"
                onGifClick={(_, event) => {
                    event?.preventDefault()
                    event?.stopPropagation()
                    onAdMediaClick({
                        clickTrackingUrl: click_tracking_url,
                        clickUrl: click_url,
                        gifUrl: gif.url,
                    })
                }}
            />
        </>
    )
}

const CarouselGif = ({ data, height }: { data: AdDecision; height: number }) => {
    const { gif, impression_url, click_tracking_url, click_url } = data
    const onAdMediaClick = useAdMediaClick()
    return (
        <Gif
            gif={gif}
            key={gif.id}
            height={height}
            width={getGifWidth(gif, height)}
            className="shrink-0"
            hideAttribution
            noLink
            onGifClick={(_, event) => {
                event?.preventDefault()
                event?.stopPropagation()
                onAdMediaClick({
                    clickTrackingUrl: click_tracking_url,
                    clickUrl: click_url,
                    gifUrl: gif.url,
                })
            }}
            onGifSeen={() => {
                try {
                    if (impression_url) fetch(impression_url)
                } catch (_e) {}
            }}
        />
    )
}

const carouselHeight = 124
const carouselId = 'giphy-xl-banner-carousel'

const SpotlightGrid = ({ data }: { data: AdDecision }) => {
    const { deviceType } = useContext(UAParserContext)
    return (
        <>
            <MainGif data={data} width={spotlightWidth} height={getGifHeight(data.gif, spotlightWidth)} />
            <ScrollCarousel
                height={carouselHeight}
                width={spotlightWidth}
                elementId={carouselId}
                deviceType={deviceType}
            >
                {data.subdecisions?.map((decision) => {
                    return <CarouselGif key={decision.gif.id} data={decision} height={carouselHeight} />
                })}
            </ScrollCarousel>
        </>
    )
}

export default SpotlightGrid
